import { BarsOutlined, ShopOutlined } from "@ant-design/icons";
import { Button, InputNumber } from "antd";
import Table from "../../general/table-components/table";
import { ColumnProps } from "antd/lib/table";
import moment from "moment";
import React, { useContext, useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { DndContext, DragEndEvent, closestCenter } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import HomePageSectionsArrangementContext from "../../../context/arrange-sections-page-home/context";
import { IHomePageSectionsArrangementList } from "../../../models/arrange_sections_page_home/response";
import EditBtn from "../../general/table-components/actions/edit-btn";
import Space from "../../general/antd/space";
import DeleteBtn from "../../general/table-components/actions/delete-btn";
import eventManager, { EVENT_SUCCESS } from "../../../utils/events";
import styles from './styles.module.scss'
import Tag from "../../general/antd/tag";
import { getSectionStatus, getSectionStatusColor, SectionStatus } from "../../../models/arrange_sections_page_home/enum";
interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: any;
}
const RowContext = React.createContext<RowContextProps>({});

const DragHandle = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<BarsOutlined />}
      style={{ cursor: "grab" }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

const Row: React.FC<{ "data-row-key": string } & React.HTMLAttributes<HTMLTableRowElement>> = (props) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: props["data-row-key"],
  });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: "relative", zIndex: 9999 } : {}),
  };

  const contextValue = useMemo(() => ({ setActivatorNodeRef, listeners }), [setActivatorNodeRef, listeners]);

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

const HomePageSectionsArrangementTable = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { list, actions, loading, query } = useContext(HomePageSectionsArrangementContext);
  const [dataSource, setDataSource] = useState(list?.data ?? []);
  const [loadingUpdate, setLoadingUpdate] = useState(true);

  useEffect(() => {
    if (list?.data) {
      setDataSource(list.data);
      setLoadingUpdate(false);
    }
  }, [list?.data]);

  const onDragEnd = async ({ active, over }: DragEndEvent) => {
    if (!over) return;

    const activeId = Number(active.id);
    const overId = Number(over.id);

    const movedItem = dataSource.find((item) => item.id === activeId);
    const overItem = dataSource.find((item) => item.id === overId);

    if (movedItem && overItem) {
      const updatedDataSource = arrayMove(dataSource, dataSource.indexOf(movedItem), dataSource.indexOf(overItem));
      setDataSource(updatedDataSource);

      await handlePriorityChange(movedItem.id, overItem.priority);
    }
  };

  const handlePriorityChange = async (id: number, newPriority: number | null) => {
    if (newPriority === null) return;

    const updatedDataSource = dataSource.map((item) =>
      item.id === id ? { ...item, priority: newPriority } : item
    );

    const sortedDataSource = [...updatedDataSource].sort((a, b) => a.priority - b.priority);
    setDataSource(sortedDataSource);
    await actions.updateHomePageSectionsArrangement(id, { priority: newPriority });
    await actions.getData();
  };

const columns: ColumnProps<IHomePageSectionsArrangementList>[] = [
    { key: "sort", align: "center", width: 80, render: () => <DragHandle /> },
    { title: t("id"), dataIndex: "id", key: "id", align: "center" },
    { title: t("title"), dataIndex: "title", key: "title", align: "center" },
    { title: t("key"), dataIndex: "key", key: "key", align: "center" },
    {
      title: t("priority"),
      dataIndex: "priority",
      key: "priority",
      align: "center",
      render: (priority, record) => {
        const currentItem = dataSource.find((item) => item.id === record.id);
        return (
          <InputNumber
            min={1}
            max={100}
            value={currentItem ? currentItem.priority : priority}
            onChange={(value) => handlePriorityChange(record.id, value)}
          />
        );
      },
    },
    {
          title: t("status"),
          dataIndex: "status",
          key: "status",
          align: "center",
          render: (status: SectionStatus) => (
            <Tag
              color={getSectionStatusColor(status)}
              title={t(getSectionStatus(status))}
            />
          ),
        },
    {
      title: t("created_at"),
      dataIndex: "createdAt",
      key: "createdAt",
      align: "center",
      render: (createdAt) => moment(createdAt).format("YYYY-MM-DD"),
    },
    {
      title: t("actions"),
      width: 250,
      dataIndex: "",
      align: "center",
      key: "x",
      render: (_: any, record) => (
        <Space>

          {(record?.sortFilter != null || record?.key === "BrandsHaveFreeDeliverySection" || record?.key === "BrandsHaveDiscountsSection") &&
            <Button
              shape="circle"
              icon={<ShopOutlined />}
              onClick={async () => {
                // Use sortFilter from record, or derive from key if not available
                const sortFilterValue = record?.sortFilter ??
                  (record?.key === "BrandsHaveFreeDeliverySection" ? "hasFreeDeliveryProducts" :
                   record?.key === "BrandsHaveDiscountsSection" ? "hasDiscountProducts" : null);
                if (sortFilterValue) {
                  navigate(`${record?.id}/companies/${sortFilterValue}`, { state: { sectionId: record?.id, sortFilter: sortFilterValue } })
                }
              }}
            />
          }
          <EditBtn onClick={(e) => {
            const url = `update/${record?.id}`;
            if (e.ctrlKey || e.metaKey) {
              window.open("home-page-arrange/" + url, "_blank");
            } else {
              navigate(url, { state: { initialValues: record } });
            }
          }} />

          {
            (record?.isBanner) &&
            <DeleteBtn
              onConfirm={async () => {
                await actions.deleteHomePageSectionsArrangement(record?.id);
                eventManager.emit(EVENT_SUCCESS);
              }}
              loading={loading.includes("delete")}
            />}
        </Space>
      ),
    },
  ];

  return (
    <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={dataSource.map((i) => i.id)} strategy={verticalListSortingStrategy}>
        <Table<IHomePageSectionsArrangementList>
          rowKey="id"
          columns={columns}
          className={styles.table}
          dataSource={dataSource}
          loading={loading.includes("list") || list == undefined}
          total={list?.total}
          size="small"
          scroll={{ x: 1400 }}
          onChange={() => { }}
          page={query.page}
          pageSize={query.perPage}
          showPagination={true}
          onPaginationChange={(page, pageSize) => {
            actions.setQuery({
              ...query,
              page,
              perPage: pageSize,
            });
          }}
          components={{
            body: {
              row: Row,
            },
          }}
        />
      </SortableContext>
    </DndContext>
  );
};

export default HomePageSectionsArrangementTable;